import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ClipLoader } from "react-spinners";

import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotInsetCardStyle,
  autopilotTitleStyle,
} from "./styles";

const DEFAULT_ERROR_COLOR = "var(--everlog-status-danger)";

function noop() {}

function AutopilotKpiCard({
  eyebrow,
  title,
  description,
  onClick,
  loading = false,
  error = null,
  hasData = false,
  emptyMessage,
  headerAside = null,
  primary,
  secondaryLines = [],
  minHeight = 140,
  style,
}) {
  const handleCardClick = onClick || noop;
  const isClickable = typeof onClick === "function";

  const cardStyle = {
    ...autopilotCardStyle,
    display: "flex",
    flexDirection: "column",
    flex: 1,
    background: "var(--everlog-card-hero-bg)",
    cursor: isClickable ? "pointer" : "default",
    ...style,
  };

  const bodyStyle = {
    ...autopilotBodyStyle,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  };

  const headerWrapStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  };

  const headerLeadStyle = {
    minWidth: 0,
    flex: 1,
  };

  const emptyStyle = {
    ...autopilotInsetCardStyle,
    padding: "14px 16px",
    color: AUTOPILOT_COLORS.muted,
    lineHeight: 1.6,
  };

  const primaryCardStyle = {
    ...autopilotInsetCardStyle,
    padding: "12px 14px",
  };

  const handleKeyDown = (event) => {
    if (!isClickable) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick();
    }
  };

  return (
    <Card
      className="card-everlog-shell"
      style={cardStyle}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <CardHeader style={autopilotHeaderStyle}>
        <div style={headerWrapStyle}>
          <div style={headerLeadStyle}>
            {eyebrow ? <div style={autopilotEyebrowStyle}>{eyebrow}</div> : null}
            {title ? (
              <div
                style={{
                  ...autopilotTitleStyle,
                  marginBottom: description ? "0.3rem" : 0,
                }}
              >
                {title}
              </div>
            ) : null}
            {description ? <p style={autopilotDescriptionStyle}>{description}</p> : null}
          </div>
          {headerAside ? <div style={{ flexShrink: 0 }}>{headerAside}</div> : null}
        </div>
      </CardHeader>

      <CardBody style={bodyStyle}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <ClipLoader size={40} color="var(--theme-accent)" loading />
          </div>
        ) : error ? (
          <p
            style={{
              color: DEFAULT_ERROR_COLOR,
              fontSize: "0.8rem",
              textAlign: "center",
            }}
          >
            {error}
          </p>
        ) : !hasData ? (
          <div style={emptyStyle}>{emptyMessage}</div>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight,
            }}
          >
            <div style={primaryCardStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <span
                  style={{
                    color: AUTOPILOT_COLORS.muted,
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {primary.label}
                </span>
                {primary.tag ? (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: AUTOPILOT_COLORS.muted,
                      fontWeight: 600,
                    }}
                  >
                    {primary.tag}
                  </span>
                ) : null}
              </div>

              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    color: AUTOPILOT_COLORS.title,
                    fontSize: primary.valueFontSize || "1.5rem",
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  {primary.value}
                </div>

                {primary.iconClassName ? (
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        primary.iconBackground || "var(--everlog-info-button-bg)",
                      border: `1px solid ${AUTOPILOT_COLORS.border}`,
                      color: primary.iconColor || "var(--theme-accent)",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={primary.iconClassName}
                      style={{ fontSize: primary.iconFontSize || "0.9rem" }}
                    />
                  </div>
                ) : null}
              </div>

              {primary.caption ? (
                <div
                  style={{
                    marginTop: 7,
                    color: primary.captionColor || AUTOPILOT_COLORS.text,
                    fontSize: primary.captionFontSize || "0.78rem",
                    fontWeight: primary.captionWeight || 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  {primary.caption}
                </div>
              ) : null}
            </div>

            {secondaryLines.length ? (
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
                }}
              >
                {secondaryLines.map((line, index) => (
                  <div
                    key={line.key || index}
                    style={{
                      marginTop: index === 0 ? 0 : 6,
                      color: line.color || AUTOPILOT_COLORS.muted,
                      fontSize: line.fontSize || "0.75rem",
                      fontWeight: line.fontWeight || 400,
                    }}
                  >
                    {line.text}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default AutopilotKpiCard;
